// HomeRedesign — PREVIEW of the redesigned staff home (Jun 17, 2026).
//
// Mounted at /home-preview (gated to admin / sales_director / super-users) so
// it can be reviewed WITHOUT touching the live "/" Home. Built from the approved
// mockup (home-redesign-mockup.html). First cut — wired to live data for the
// week schedule (adjuster meetings + builds, the focal layer the sales+roof team
// asked for), the weekly-commitment AM/PM/EVE overlay for canvasable roles, and a
// role-aware attention list / nav / quick actions / cards.
//
// Data scope: field RM/TL see their own deals; ASM + exec + Robert + Chad +
// super-users see company-wide. Times are stored "fake-UTC" (ET wall-clock tagged
// +00:00) so we read the raw digits via extractRawTime, never new Date().

import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { extractRawTime } from "../../lib/timeUtils";
import {
  aggregateMatch,
  mondayOf,
  type CommitmentMatchRow,
  type CommitmentState,
} from "../../lib/commitmentMath";
import { isCompanyViewRole, LANDING_BY_ROLE } from "./homeNav";
import { attentionFor, type AttentionItem } from "./roleAttention";
import ManualEntryModal, { type RosterMember } from "../deal/ManualEntryModal";
import { resolvePlayReps, PURSUABLE_STAGES, EXEC_ROLES } from "../../lib/playScope";
import OnboardICModal from "../admin/OnboardICModal";
import AddEventModal from "./AddEventModal";
import MyBusinessSummary from "../snapshot/MyBusinessSummary";
import CommissionGoalChart from "../snapshot/CommissionGoalChart";
import { logQuickAction } from "../../lib/uiEvents";
import { useBusinessSnapshot } from "./useBusinessSnapshot";
import { FIELD_ROLES } from "../../lib/businessSnapshot";
import { computeSpiffSummary, mondayOf as mondayOfET, type SpiffSummary } from "../../lib/weeklySpiffs";
import { fetchWeeklySpiffData } from "../crew/crewQueries";
import SpiffWeekStrip from "./SpiffWeekStrip";
import MonthBuildRace from "./MonthBuildRace";
import MyDayCard from "./MyDayCard";
import { isCrewManager } from "../crew/crewAuth";
import { CANVASABLE_ROLES } from "../../lib/dailyCommitments";
import TodayHero, { type TodayHeroItem } from "./widgets/TodayHero";
import WidgetBoard from "./widgets/WidgetBoard";
import CanvasserHome from "../canvasser/CanvasserHome";
import { resolveWidgets, widgetsForUser, type WidgetKey } from "./widgets/registry";
import { fetchWidgetLayout } from "../../lib/widgetLayouts";
import { addDaysISO as addDaysISOBuild, buildDaysIn, BUILD_SPAN_LOOKBACK_DAYS } from "../../lib/buildCalendar";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Roles that get their OWN locked 1-on-1 commitment. Managers/exec/admin don't —
// they pick a rep to view via the commitment picker.
const FIELD_COMMIT_ROLES = new Set([
  "restore_manager", "team_lead", "asm", "canvasser",
]);

// Who can open the "Log a Lead" manual-entry button (mirrors the old Home).
const MANUAL_ENTRY_ROLES = new Set([
  "restore_manager", "team_lead", "asm", "admin", "sales_director",
  "roof_director", "director_of_operations",
  "roof_team_manager", // Oct 22, 2026: Robert logs his own leads (executive path)
]);
// Roles whose owner field locks to themselves (RM/TL log their own deals).
const SELF_ASSIGN_ROLES = new Set(["restore_manager", "team_lead"]);

// Roles whose RLS (canvas_can_manage) can read OTHER reps' weekly_commitments —
// only these get the strip's "who's locked" list; mirrors the DB function.
const LOCKS_LIST_ROLES = new Set([
  "admin", "sales_director", "asm", "team_lead",
  "roof_team_manager", "roof_director", "director_of_operations",
]);

const pad = (n: number) => String(n).padStart(2, "0");
const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtTime(ts: string | null): string {
  const hhmm = ts ? extractRawTime(ts) : null;
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "p" : "a";
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return m === "00" ? `${h12}${ampm}` : `${h12}:${m}${ampm}`;
}

interface SchedItem { t: "meet" | "reinsp" | "build"; ho: string; addr: string; time: string; ownerId: string | null; splitId: string | null; dealId: string; carrier: string | null; stage: string | null; claimNo: string | null; /** "day 2 of 2" on a multi-day build (Oct 26, 2026). */ dayOf?: string; }
type Attn = AttentionItem;

const prettyStage = (s: string | null) => (s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

// Who may open another canvasser's home (?canvasser=<id>): roles that can read
// all of it — canvasser_my_pay, daily_commitments (crew_is_manager),
// weekly_commitments + canvas_area_stats (canvas_can_manage). Finance can read
// the pay but not the plan or areas, so it isn't here.
const CANVASSER_PREVIEW_ROLES = new Set(["admin", "sales_director", "director_of_operations", "asm"]);

/**
 * Home. Canvassers get their own page (Oct 27, 2026; CanvasserHome): areas,
 * Log a lead, My Pay and their numbers against their goals. Leadership can
 * look at a canvasser's page with /?canvasser=<team_roster id>.
 */
export default function HomeRedesign({ preview = false }: { preview?: boolean }) {
  const { member, isSuperUser } = useAuth();
  const [params] = useSearchParams();
  const canPreview = !!isSuperUser || (!!member?.role && CANVASSER_PREVIEW_ROLES.has(member.role));
  const previewCanvasser = canPreview ? params.get("canvasser") : null;
  if (previewCanvasser) return <CanvasserPreview memberId={previewCanvasser} />;
  if (!preview && member?.role === "canvasser") {
    return <CanvasserHome memberId={member.id} firstName={member.full_name?.split(" ")[0] ?? "there"} />;
  }
  return <StaffHome preview={preview} />;
}

function CanvasserPreview({ memberId }: { memberId: string }) {
  const [name, setName] = useState<string | null>(null);
  useEffect(() => {
    supabase.from("team_roster").select("full_name").eq("id", memberId).maybeSingle()
      .then(({ data }) => setName((data?.full_name as string | undefined)?.split(" ")[0] ?? "Canvasser"));
  }, [memberId]);
  if (!name) return <div className="max-w-2xl mx-auto px-4 py-10 text-sm text-slate-400">Loading…</div>;
  return <CanvasserHome memberId={memberId} firstName={name} preview />;
}

function StaffHome({ preview = false }: { preview?: boolean }) {
  const { member, role, isSuperUser } = useAuth();
  const [params] = useSearchParams();
  const isCompanyView = isCompanyViewRole(role, !!isSuperUser);

  // Header quick-actions ported from the old Home (preserved on promotion):
  // Log a Lead (manual entry) + Onboard IC (super-users).
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showOnboardIC, setShowOnboardIC] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [rosterMembers, setRosterMembers] = useState<RosterMember[]>([]);
  const canManualEntry = isSuperUser || (!!role && MANUAL_ENTRY_ROLES.has(role));
  useEffect(() => {
    if (!canManualEntry) return;
    supabase.from("team_roster").select("id,full_name,role").eq("is_active", true).order("full_name")
      .then(({ data }) => { if (data) setRosterMembers(data.map((m: any) => ({ id: m.id, full_name: m.full_name, role: m.role ?? "" }))); });
  }, [canManualEntry]);

  // Run the Play badge — count of pursuable leads in this viewer's scope.
  // Uses the SAME shared resolver as the Run the Play page so they can't drift.
  const [playCount, setPlayCount] = useState<number | null>(null);
  useEffect(() => {
    if (!member || !canManualEntry) return;
    let cancelled = false;
    (async () => {
      const { data: rdata } = await supabase
        .from("team_roster").select("*").eq("is_active", true)
        .in("role", ["restore_manager", "team_lead", "asm"]);
      if (cancelled) return;
      const isExec = !!isSuperUser || EXEC_ROLES.has(role ?? "");
      const ids = resolvePlayReps(
        { id: member.id, full_name: member.full_name, role: member.role },
        (rdata ?? []) as any,
        isExec,
      ).map((r) => r.id);
      if (ids.length === 0) { if (!cancelled) setPlayCount(0); return; }
      const inList = `(${ids.join(",")})`;
      const { count } = await supabase
        .from("deals").select("id", { count: "exact", head: true })
        .in("stage", PURSUABLE_STAGES as unknown as string[])
        .or(`owner_id.in.${inList},split_rm_id.in.${inList}`);
      if (!cancelled) setPlayCount(count ?? 0);
    })();
    return () => { cancelled = true; };
  }, [member, canManualEntry, isSuperUser, role]);

  // Role-landing redirect target, computed early so the data hooks below skip
  // fetching for users who never see this page (React still flushes a
  // committed render's effects even when it returns <Navigate/>).
  // Ship 2: a user with a home widget board (role default OR a hand-set
  // per-person override — Sam's dual-role board retires his /my-work redirect)
  // STAYS on home instead of bouncing to a specialized board.
  const landingPrimary = member?.role ? LANDING_BY_ROLE[member.role] : undefined;

  // Saved board from /team-boards (user_widget_layout). Beats the registry
  // defaults; null = nothing saved. Realtime so a board edited for someone
  // lands in their open tab without a refresh.
  const [customKeys, setCustomKeys] = useState<WidgetKey[] | null>(null);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  useEffect(() => {
    if (!member?.id) return;
    let cancelled = false;
    const load = () =>
      fetchWidgetLayout(member.id)
        .then((keys) => { if (!cancelled) { setCustomKeys(keys); setLayoutLoaded(true); } })
        .catch(() => { if (!cancelled) setLayoutLoaded(true); });
    void load();
    const ch = supabase
      .channel(`uwl-${member.id}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "user_widget_layout", filter: `member_id=eq.${member.id}` },
        () => { void load(); })
      .subscribe();
    return () => { cancelled = true; void supabase.removeChannel(ch); };
  }, [member?.id]);

  // Code-default board (synchronous). Only someone with a LANDING_BY_ROLE
  // redirect AND no code board has to wait on the saved-layout fetch before we
  // know whether to bounce them — everyone else renders immediately and their
  // board fills in when the row lands.
  const codeBoard = member ? widgetsForUser(member.id, member.role, member.email) : [];
  const boardKeys = member
    ? resolveWidgets(customKeys, member.id, member.role, member.email)
    : [];
  const hasHomeBoard = boardKeys.length > 0;
  const mightRedirect = !preview && !!landingPrimary && codeBoard.length === 0;
  const willRedirect = mightRedirect && layoutLoaded && !hasHomeBoard;
  // Don't start the heavy home fetches for someone we may be about to bounce.
  const holdForLayout = mightRedirect && !layoutLoaded;

  // Business snapshot — fetched ONCE here and shared by the spiff strip, the
  // widget board, and the My Business zone (one load, three consumers).
  const { core: snapCore, error: snapError } = useBusinessSnapshot(willRedirect || holdForLayout ? null : member, role, !!isSuperUser);

  // Current-week spiff race (ET week) — shared by the strip + the Today hero
  // nudge. All thresholds live in lib/weeklySpiffs; nothing is re-derived here.
  const spiffWeekMonday = useMemo(() => mondayOfET(new Date()), []);
  const [spiffSummary, setSpiffSummary] = useState<SpiffSummary | null>(null);
  useEffect(() => {
    if (!member || willRedirect || holdForLayout) return;
    let cancelled = false;
    fetchWeeklySpiffData(spiffWeekMonday)
      .then(({ activity, members, reviews }) => {
        if (!cancelled) setSpiffSummary(computeSpiffSummary(activity, members, spiffWeekMonday, reviews));
      })
      .catch(() => { /* strip renders without the race */ });
    return () => { cancelled = true; };
  }, [member?.id, spiffWeekMonday, holdForLayout]); // eslint-disable-line react-hooks/exhaustive-deps

  // Monday of the current week, and the offset-adjusted visible week.
  const baseMonday = useMemo(() => {
    const [y, m, d] = mondayOf(new Date()).split("-").map(Number);
    return new Date(y, m - 1, d);
  }, []);
  const todayISO = isoDate(new Date());

  // Week vs Month view (optional month calendar). Week is the default.
  const [viewMode, setViewMode] = useState<"week" | "month">("week");
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [selDay, setSelDay] = useState<number>(() => (new Date().getDay() + 6) % 7);
  const [selMonthISO, setSelMonthISO] = useState<string>(() => isoDate(new Date()));

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const dt = new Date(baseMonday);
      dt.setDate(dt.getDate() + weekOffset * 7 + i);
      return dt;
    });
  }, [baseMonday, weekOffset]);
  const dayISOs = days.map(isoDate);
  const mondayStr = dayISOs[0];
  const isCurrentWeek = weekOffset === 0;

  // Month grid — 6 rows × 7 cols starting on the Monday on/before the 1st.
  const monthBase = useMemo(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth() + monthOffset, 1);
  }, [monthOffset]);
  const monthCells = useMemo(() => {
    const firstDow = (monthBase.getDay() + 6) % 7;
    const start = new Date(monthBase);
    start.setDate(monthBase.getDate() - firstDow);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [monthBase]);
  const monthStartISO = isoDate(monthCells[0]);
  const monthEndISO = isoDate(monthCells[41]);

  // ── per-day schedule (meetings + builds) ──────────────────────────────────
  const [sched, setSched] = useState<SchedItem[][]>(() => Array.from({ length: 7 }, () => []));
  const [loadingWeek, setLoadingWeek] = useState(true);
  const [monthSched, setMonthSched] = useState<Record<string, SchedItem[]>>({});
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [roster, setRoster] = useState<Record<string, string>>({});

  // roster id → full name, for labeling each appointment's RM + split RM.
  // Also carries role/team links so the TL/ASM scope toggle can resolve teams.
  const [rosterRows, setRosterRows] = useState<Array<{ id: string; full_name: string; role: string; team_lead_id: string | null; asm_id: string | null }>>([]);
  useEffect(() => {
    supabase.from("team_roster").select("id,full_name,role,team_lead_id,asm_id").eq("is_active", true).then(({ data }) => {
      const m: Record<string, string> = {};
      (data ?? []).forEach((r: any) => { m[r.id] = r.full_name; });
      setRoster(m);
      setRosterRows((data ?? []) as any);
    });
  }, []);

  // ── schedule scope (Ship 6) ───────────────────────────────────────────────
  // TL: Mine | Team (default Team — the whole point of the fix is that a TL's
  // home finally shows their RMs' schedule). ASM: Mine | My teams | Company
  // (default Company — today's behavior). Everyone else keeps the existing
  // rule (company-view roles company-wide, field roles own-only).
  type HomeScope = "mine" | "team" | "company";
  const scopeChoices: HomeScope[] = role === "team_lead" ? ["mine", "team"] : role === "asm" ? ["mine", "team", "company"] : [];
  const [homeScope, setHomeScope] = useState<HomeScope>(() => {
    try {
      const raw = localStorage.getItem(`bp_home_scope_${member?.id ?? ""}`) as HomeScope | null;
      if (raw === "mine" || raw === "team" || raw === "company") return raw;
    } catch { /* default below */ }
    return role === "team_lead" ? "team" : "company";
  });
  const pickScope = (s: HomeScope) => {
    setHomeScope(s);
    try { localStorage.setItem(`bp_home_scope_${member?.id ?? ""}`, s); } catch { /* per-device best-effort */ }
  };
  // null = no owner filter (company-wide); else the owner/split id list.
  const schedScopeIds = useMemo<string[] | null>(() => {
    if (!member) return null;
    if (role === "team_lead") {
      if (homeScope === "mine") return [member.id];
      const team = rosterRows.filter((r) => r.team_lead_id === member.id).map((r) => r.id);
      return [member.id, ...team];
    }
    if (role === "asm") {
      if (homeScope === "company") return null;
      if (homeScope === "mine") return [member.id];
      const ids = new Set<string>([member.id]);
      for (const r of rosterRows) {
        if (r.asm_id === member.id || r.team_lead_id === member.id) ids.add(r.id);
      }
      return [...ids];
    }
    return isCompanyView ? null : [member.id];
  }, [member, role, homeScope, rosterRows, isCompanyView]);
  const scopeKey = schedScopeIds ? schedScopeIds.join(",") : "company";
  // assigned_rs_id leg: an RS with a home board (Sam) has 0 owned deals but
  // real assigned meetings — without it his week hero is permanently empty.
  // No-op for RM/TL/ASM scopes (their ids are never assigned_rs_id).
  const scopeOrOf = (ids: string[] | null) =>
    ids
      ? `owner_id.in.(${ids.join(",")}),split_rm_id.in.(${ids.join(",")}),assigned_rs_id.in.(${ids.join(",")})`
      : null;

  useEffect(() => {
    let cancelled = false;
    setLoadingWeek(true);
    (async () => {
      const wStart = `${dayISOs[0]}T00:00:00Z`;
      const wEnd = `${dayISOs[6]}T23:59:59Z`;
      const scopeOr = scopeOrOf(schedScopeIds);

      let mq = supabase.from("deals")
        .select("id,homeowner_name,property_street,property_city,adjuster_meeting_date,owner_id,split_rm_id,insurance_company,stage,claim_number")
        .gte("adjuster_meeting_date", wStart).lte("adjuster_meeting_date", wEnd);
      let rq = supabase.from("deals")
        .select("id,homeowner_name,property_street,property_city,reinspection_adjuster_meeting_date,owner_id,split_rm_id,insurance_company,stage,claim_number")
        .gte("reinspection_adjuster_meeting_date", wStart).lte("reinspection_adjuster_meeting_date", wEnd);
      // Builds that started up to a month back still show on each day they run (Oct 26, 2026).
      let bq = supabase.from("deals")
        .select("id,homeowner_name,property_street,property_city,build_scheduled_date,build_end_date,owner_id,split_rm_id,insurance_company,stage,claim_number")
        .gte("build_scheduled_date", addDaysISOBuild(dayISOs[0], -BUILD_SPAN_LOOKBACK_DAYS)).lte("build_scheduled_date", dayISOs[6]);
      if (scopeOr) { mq = mq.or(scopeOr); rq = rq.or(scopeOr); bq = bq.or(scopeOr); }

      const [meets, reinsps, builds] = await Promise.all([mq, rq, bq]);
      if (cancelled) return;

      const next: SchedItem[][] = Array.from({ length: 7 }, () => []);
      const addrOf = (r: any) => [r.property_street, r.property_city].filter(Boolean).join(", ");
      const splitOf = (r: any) => (r.split_rm_id && r.split_rm_id !== r.owner_id ? r.split_rm_id : null);
      for (const r of meets.data ?? []) {
        const idx = dayISOs.indexOf(String(r.adjuster_meeting_date).slice(0, 10));
        if (idx >= 0) next[idx].push({ t: "meet", ho: r.homeowner_name ?? "(no name)", addr: addrOf(r), time: fmtTime(r.adjuster_meeting_date), ownerId: r.owner_id ?? null, splitId: splitOf(r), dealId: r.id, carrier: r.insurance_company ?? null, stage: r.stage ?? null, claimNo: r.claim_number ?? null });
      }
      for (const r of reinsps.data ?? []) {
        const idx = dayISOs.indexOf(String(r.reinspection_adjuster_meeting_date).slice(0, 10));
        if (idx >= 0) next[idx].push({ t: "reinsp", ho: r.homeowner_name ?? "(no name)", addr: addrOf(r), time: fmtTime(r.reinspection_adjuster_meeting_date), ownerId: r.owner_id ?? null, splitId: splitOf(r), dealId: r.id, carrier: r.insurance_company ?? null, stage: r.stage ?? null, claimNo: r.claim_number ?? null });
      }
      for (const r of builds.data ?? []) {
        for (const bd of buildDaysIn(r.build_scheduled_date, r.build_end_date, dayISOs[0], dayISOs[6])) {
          const idx = dayISOs.indexOf(bd.iso);
          if (idx >= 0) next[idx].push({ t: "build", ho: r.homeowner_name ?? "(no name)", addr: addrOf(r), time: "", ownerId: r.owner_id ?? null, splitId: splitOf(r), dealId: r.id, carrier: r.insurance_company ?? null, stage: r.stage ?? null, claimNo: r.claim_number ?? null, dayOf: bd.of > 1 ? `day ${bd.day} of ${bd.of}` : undefined });
        }
      }
      // earliest-first within each day so the week-card mini-agenda + list read in order
      for (const day of next) day.sort((a, b) => (a.time || "z").localeCompare(b.time || "z"));
      setSched(next);
      setLoadingWeek(false);
    })();
    return () => { cancelled = true; };
  }, [mondayStr, scopeKey, member?.id, refreshTick]);

  // ── month schedule (only fetched while the Month view is open) ─────────────
  useEffect(() => {
    if (viewMode !== "month") return;
    let cancelled = false;
    setLoadingMonth(true);
    (async () => {
      const wStart = `${monthStartISO}T00:00:00Z`;
      const wEnd = `${monthEndISO}T23:59:59Z`;
      const scopeOr = scopeOrOf(schedScopeIds);
      let mq = supabase.from("deals")
        .select("id,homeowner_name,property_street,property_city,adjuster_meeting_date,owner_id,split_rm_id,insurance_company,stage,claim_number")
        .gte("adjuster_meeting_date", wStart).lte("adjuster_meeting_date", wEnd);
      let rq = supabase.from("deals")
        .select("id,homeowner_name,property_street,property_city,reinspection_adjuster_meeting_date,owner_id,split_rm_id,insurance_company,stage,claim_number")
        .gte("reinspection_adjuster_meeting_date", wStart).lte("reinspection_adjuster_meeting_date", wEnd);
      let bq = supabase.from("deals")
        .select("id,homeowner_name,property_street,property_city,build_scheduled_date,build_end_date,owner_id,split_rm_id,insurance_company,stage,claim_number")
        .gte("build_scheduled_date", addDaysISOBuild(monthStartISO, -BUILD_SPAN_LOOKBACK_DAYS)).lte("build_scheduled_date", monthEndISO);
      if (scopeOr) { mq = mq.or(scopeOr); rq = rq.or(scopeOr); bq = bq.or(scopeOr); }
      const [meets, reinsps, builds] = await Promise.all([mq, rq, bq]);
      if (cancelled) return;
      const map: Record<string, SchedItem[]> = {};
      const addrOf = (r: any) => [r.property_street, r.property_city].filter(Boolean).join(", ");
      const splitOf = (r: any) => (r.split_rm_id && r.split_rm_id !== r.owner_id ? r.split_rm_id : null);
      for (const r of meets.data ?? []) {
        const iso = String(r.adjuster_meeting_date).slice(0, 10);
        (map[iso] ??= []).push({ t: "meet", ho: r.homeowner_name ?? "(no name)", addr: addrOf(r), time: fmtTime(r.adjuster_meeting_date), ownerId: r.owner_id ?? null, splitId: splitOf(r), dealId: r.id, carrier: r.insurance_company ?? null, stage: r.stage ?? null, claimNo: r.claim_number ?? null });
      }
      for (const r of reinsps.data ?? []) {
        const iso = String(r.reinspection_adjuster_meeting_date).slice(0, 10);
        (map[iso] ??= []).push({ t: "reinsp", ho: r.homeowner_name ?? "(no name)", addr: addrOf(r), time: fmtTime(r.reinspection_adjuster_meeting_date), ownerId: r.owner_id ?? null, splitId: splitOf(r), dealId: r.id, carrier: r.insurance_company ?? null, stage: r.stage ?? null, claimNo: r.claim_number ?? null });
      }
      for (const r of builds.data ?? []) {
        for (const bd of buildDaysIn(r.build_scheduled_date, r.build_end_date, monthStartISO, monthEndISO)) {
          (map[bd.iso] ??= []).push({ t: "build", ho: r.homeowner_name ?? "(no name)", addr: addrOf(r), time: "", ownerId: r.owner_id ?? null, splitId: splitOf(r), dealId: r.id, carrier: r.insurance_company ?? null, stage: r.stage ?? null, claimNo: r.claim_number ?? null, dayOf: bd.of > 1 ? `day ${bd.day} of ${bd.of}` : undefined });
        }
      }
      for (const iso of Object.keys(map)) map[iso].sort((a, b) => (a.time || "z").localeCompare(b.time || "z"));
      setMonthSched(map);
      setLoadingMonth(false);
    })();
    return () => { cancelled = true; };
  }, [viewMode, monthStartISO, monthEndISO, scopeKey, member?.id, refreshTick]);

  // ── weekly-commitment overlay ─────────────────────────────────────────────
  // Shows ONE rep's locked AM/PM/EVE plan on the week strip. Field reps (RM/TL/
  // ASM/canvasser) see their own; managers/exec/admin pick a rep (they have no
  // plan of their own). Picker renders for company-view roles.
  const [commitState, setCommitState] = useState<Record<number, Record<string, CommitmentState>>>({});
  const [commitStats, setCommitStats] = useState<ReturnType<typeof aggregateMatch> | null>(null);
  const [streak, setStreak] = useState<number>(0);
  const [reps, setReps] = useState<Array<{ id: string; full_name: string }>>([]);
  const [commitUserId, setCommitUserId] = useState<string | null>(null);

  // canvasable reps for the manager picker
  useEffect(() => {
    if (!isCompanyView) return;
    supabase.from("team_roster").select("id,full_name")
      .eq("is_active", true).in("role", ["restore_manager", "team_lead", "asm", "canvasser"])
      .order("full_name")
      .then(({ data }) => setReps((data ?? []) as Array<{ id: string; full_name: string }>));
  }, [isCompanyView]);

  // default target: self if a field-commitment role, else the first rep (managers)
  useEffect(() => {
    if (commitUserId || !member) return;
    if (role && FIELD_COMMIT_ROLES.has(role)) setCommitUserId(member.id);
    else if (reps.length) setCommitUserId(reps[0].id);
  }, [commitUserId, member, role, reps]);

  useEffect(() => {
    if (!commitUserId) { setCommitState({}); setCommitStats(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("weekly_commitment_match")
        .select("day_offset,block_label,committed,state")
        .eq("user_id", commitUserId).eq("week_start", mondayStr);
      if (cancelled) return;
      const rows = (data ?? []) as Pick<CommitmentMatchRow, "day_offset" | "block_label" | "committed" | "state">[];
      const grid: Record<number, Record<string, CommitmentState>> = {};
      for (const r of rows) {
        (grid[r.day_offset] ??= {})[r.block_label] = r.state;
      }
      setCommitState(grid);
      setCommitStats(rows.length ? aggregateMatch(rows as CommitmentMatchRow[]) : null);
    })();
    return () => { cancelled = true; };
  }, [mondayStr, commitUserId]);

  useEffect(() => {
    if (!commitUserId) { setStreak(0); return; }
    supabase.from("team_roster").select("commitment_streak_weeks").eq("id", commitUserId).maybeSingle()
      .then(({ data }) => { setStreak(data ? ((data as any).commitment_streak_weeks ?? 0) : 0); });
  }, [commitUserId]);

  // ── attention: per-role "things to address" (roleAttention engine) ─────────
  // Super-users can preview another role's list via ?as=<role>. Counts use the
  // viewer's id — real for company-wide queues, illustrative for owned-deal items.
  const attnRole = (isSuperUser ? params.get("as") : null) || role;
  const [attn, setAttn] = useState<Attn[]>([]);
  useEffect(() => {
    if (!member) return;
    let cancelled = false;
    attentionFor(attnRole, member.id).then((items) => { if (!cancelled) setAttn(items); });
    return () => { cancelled = true; };
  }, [member?.id, attnRole]);
  const toAddress = attn.reduce((s, a) => s + a.count, 0);

  if (!member) return null;

  // A landing-role user with no code board could still have been given one on
  // /team-boards — hold one beat for that row instead of bouncing them off a
  // board someone just built for them.
  if (holdForLayout) {
    return <div className="max-w-2xl mx-auto px-4 py-10 text-sm text-slate-400">Loading…</div>;
  }

  // Role landings (keyed on roster role, so they apply even to super-users).
  // Skipped on the /home-preview mount so leadership can always review it.
  //  • one specialized role → straight to that board (RS → Dispatch, etc.)
  if (willRedirect) {
    if (landingPrimary) return <Navigate to={landingPrimary} replace />;
  }

  const firstName = member.full_name?.split(" ")[0] ?? "there";
  const defaultOwnerId = canManualEntry && role && SELF_ASSIGN_ROLES.has(role) ? (member.id ?? null) : null;

  const BLOCK_LETTER = ["am", "pm", "eve"].map((b, i) => [b, ["A", "P", "E"][i]] as const);
  const cellClass = (st: CommitmentState | undefined, today: boolean): string => {
    if (st === "committed_showed") return "bg-emerald-500 text-white";
    if (st === "committed_future") return today ? "bg-white/20 text-white border border-white/60" : "bg-white text-orange-600 border border-orange-500";
    if (st === "committed_missed") return "bg-slate-200 text-slate-400 line-through";
    return today ? "bg-white/15 text-white/60" : "bg-slate-100 text-slate-300";
  };

  const isMonth = viewMode === "month";
  const parseISO = (iso: string) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };
  const selItems = isMonth ? (monthSched[selMonthISO] ?? []) : (sched[selDay] ?? []);
  const selDate = isMonth ? parseISO(selMonthISO) : days[selDay];
  const selIsToday = isoDate(selDate) === todayISO;
  const selDow = (selDate.getDay() + 6) % 7;
  const selMeet = selItems.filter((i) => i.t !== "build").length;
  const selBuild = selItems.filter((i) => i.t === "build").length;
  const loadingSel = isMonth ? loadingMonth : loadingWeek;
  const weekMeet = sched.reduce((s, d) => s + d.filter((i) => i.t !== "build").length, 0);
  // A multi-day build counts once for the week / month, not once per day.
  const weekBuild = new Set(sched.flatMap((d) => d.filter((i) => i.t === "build").map((i) => i.dealId))).size;
  const monthMeet = monthCells.reduce((s, dt) => dt.getMonth() === monthBase.getMonth() ? s + (monthSched[isoDate(dt)]?.filter((i) => i.t !== "build").length ?? 0) : s, 0);
  const monthBuild = new Set(monthCells.flatMap((dt) => dt.getMonth() === monthBase.getMonth() ? (monthSched[isoDate(dt)] ?? []).filter((i) => i.t === "build").map((i) => i.dealId) : [])).size;
  const periodLabel = isMonth ? "This month" : "This week";
  const periodMeet = isMonth ? monthMeet : weekMeet;
  const periodBuild = isMonth ? monthBuild : weekBuild;
  const commitRepName = isCompanyView ? reps.find((r) => r.id === commitUserId)?.full_name.split(" ")[0] : null;

  // Ship 1 (widget board) derived bits.
  const isFieldSales = !!role && FIELD_ROLES.has(role);
  const myStat = spiffSummary ? spiffSummary.perRM[member.id] ?? null : null;
  const todayDowIdx = (new Date().getDay() + 6) % 7;
  const todayItems: TodayHeroItem[] = (!isMonth && isCurrentWeek ? sched[todayDowIdx] ?? [] : []).map(
    (it) => ({ t: it.t, ho: it.ho, addr: it.addr, time: it.time, dealId: it.dealId }),
  );
  const scopeNote =
    scopeChoices.length > 0
      ? homeScope === "mine" ? "Mine" : homeScope === "team" ? (role === "asm" ? "My teams" : "Team") : "Company"
      : null;
  // The Today hero honors the registry's role gate (sales roles only) — a
  // company-view role's hero would list every meeting in the company.
  const showTodayHero = boardKeys.includes("today");
  // Pipeline widget scope: "My Pipeline" means MY deals. An ASM's schedule
  // scope defaults to Company (null) — the widget falls back to their own
  // book instead (Greg: "he still has his own pipeline to cover").
  const pipelineScopeIds = schedScopeIds ?? (isFieldSales ? [member.id] : null);
  const pipelineScopeNote =
    scopeChoices.length > 0 ? (schedScopeIds === null ? "Mine" : scopeNote) : null;
  // 1-on-1 locks card: list for manager-tier viewers whose RLS can actually
  // read other reps' rows; self-status for RMs; hidden for everyone else
  // (their RLS-filtered list read would silently look empty).
  const locksView: "self" | "list" | "none" =
    role === "restore_manager"
      ? "self"
      : isSuperUser || (!!role && LOCKS_LIST_ROLES.has(role))
        ? "list"
        : "none";

  return (
    <div className="max-w-2xl lg:max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
      {showManualEntry && (
        <ManualEntryModal
          onClose={() => setShowManualEntry(false)}
          onCreated={() => setShowManualEntry(false)}
          rosterMembers={rosterMembers}
          actorId={member.id ?? null}
          defaultOwnerId={defaultOwnerId}
        />
      )}
      {showOnboardIC && (
        <OnboardICModal onClose={() => setShowOnboardIC(false)} onCreated={() => setShowOnboardIC(false)} />
      )}
      {showAddEvent && (
        <AddEventModal
          onClose={() => setShowAddEvent(false)}
          onCreated={() => { setShowAddEvent(false); setRefreshTick((t) => t + 1); }}
          actorName={member.full_name ?? null}
        />
      )}

      {/* preview banner — only on the /home-preview review mount */}
      {preview && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          🔭 <strong>Preview</strong> — redesigned home for review. The live home is unchanged.
        </div>
      )}

      {/* Mobile-only Today hero — Greg: "I don't want to have to swipe down to
          look at what I can do that day." First screenful = today's schedule,
          the fastest jumps into work, and the live spiff nudge. Desktop keeps
          the day-detail column instead. */}
      {showTodayHero && !isMonth && isCurrentWeek && (
        <div className="lg:hidden">
          <TodayHero
            items={todayItems}
            loading={loadingWeek}
            myStat={isFieldSales ? myStat : null}
            showWorkLinks={isFieldSales}
          />
        </div>
      )}

      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-bardin-ink">Welcome, {firstName}</h1>
          <p className="text-sm text-slate-500">Here's your week.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canManualEntry && (
            <Link to="/run-the-play" onClick={() => logQuickAction("run_the_play")}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-bardin-teal text-white text-sm font-semibold hover:opacity-90 transition shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              Run the Play
              {playCount != null && playCount > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-white/25 text-white text-[11px] font-bold leading-none">{playCount}</span>
              )}
            </Link>
          )}
          {canManualEntry && (
            <button onClick={() => { logQuickAction("add_event"); setShowAddEvent(true); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-bardin-orange text-white text-sm font-semibold hover:opacity-90 transition shadow-sm">
              <span className="text-base leading-none">+</span> Add Event
            </button>
          )}
          {(canManualEntry || isSuperUser) && (
            <div className="relative">
              <button onClick={() => setMoreOpen((o) => !o)}
                className="flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 bg-white text-slate-500 text-lg font-bold hover:bg-slate-50 transition"
                aria-label="More actions" aria-expanded={moreOpen}>⋯</button>
              {moreOpen && (
                <>
                  {/* click-away backdrop */}
                  <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} />
                  <div className="absolute right-0 mt-1 z-50 w-44 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden py-1">
                    {canManualEntry && (
                      <button onClick={() => { logQuickAction("log_lead"); setMoreOpen(false); setShowManualEntry(true); }}
                        className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                        + Log a Lead
                      </button>
                    )}
                    {isSuperUser && (
                      <button onClick={() => { logQuickAction("onboard_ic"); setMoreOpen(false); setShowOnboardIC(true); }}
                        className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                        + Onboard IC
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Monthly build race (Oct 2, 2026) — front and center all month: each
          person's builds vs the goal set at the first-Monday 1-on-1. */}
      <MonthBuildRace meId={member.id} showDailyLink={isCrewManager(member)} />

      {/* The rep's own confirmed day: blocks, team knock, today's goals vs done. */}
      {role && (CANVASABLE_ROLES as readonly string[]).includes(role) && <MyDayCard meId={member.id} />}

      {/* Spiff Week strip — Monday-morning recognition (auto-hides at noon ET),
          the live race with names as people reach each spiff, weekly history,
          goals (Mine|Company) and 1-on-1 plan locks. Collapses to one line on
          phones — the Today hero owns the first screenful there. */}
      <SpiffWeekStrip
        member={{ id: member.id, full_name: member.full_name }}
        summary={spiffSummary}
        weekMonday={spiffWeekMonday}
        myFunnel={snapCore?.funnel ?? null}
        myCommitLocked={snapCore ? !!snapCore.commitmentRow?.locked_at : null}
        locksView={locksView}
        rosterRows={rosterRows}
      />

      {/* view toggle: Week / Month + (TL/ASM) schedule scope */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Your {isMonth ? "month" : "week"}</div>
          {scopeChoices.length > 0 && (
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-[10px] font-bold">
              {scopeChoices.map((s, i) => (
                <button key={s} onClick={() => pickScope(s)}
                  className={"px-2 py-1 " + (i > 0 ? "border-l border-slate-200 " : "") +
                    (homeScope === s ? "bg-bardin-teal text-white" : "bg-white text-slate-500 hover:bg-slate-50")}>
                  {s === "mine" ? "Mine" : s === "team" ? (role === "asm" ? "My teams" : "Team") : "Company"}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-[11px] font-bold">
          <button onClick={() => setViewMode("week")} className={"px-3 py-1.5 " + (!isMonth ? "bg-bardin-ink text-white" : "bg-white text-slate-500 hover:bg-slate-50")}>Week</button>
          <button onClick={() => setViewMode("month")} className={"px-3 py-1.5 border-l border-slate-200 " + (isMonth ? "bg-bardin-ink text-white" : "bg-white text-slate-500 hover:bg-slate-50")}>Month</button>
        </div>
      </div>

      {!isMonth && (
      <>
      {/* week slider */}
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => setWeekOffset((o) => Math.max(o - 1, -4))} disabled={weekOffset <= -4}
          className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-lg disabled:opacity-30">‹</button>
        <div className="text-center">
          <div className="text-sm font-extrabold text-bardin-ink">{MONTHS[days[0].getMonth()]} {days[0].getDate()} – {MONTHS[days[6].getMonth()]} {days[6].getDate()}</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-bardin-orange">
            {weekOffset === 0 ? "This week" : weekOffset === 1 ? "Next week" : weekOffset === -1 ? "Last week" : weekOffset > 0 ? `In ${weekOffset} weeks` : `${-weekOffset} weeks ago`}
          </div>
        </div>
        <button onClick={() => setWeekOffset((o) => Math.min(o + 1, 4))} disabled={weekOffset >= 4}
          className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-lg disabled:opacity-30">›</button>
      </div>

      {/* commitment rep picker — managers/exec/admin choose whose 1-on-1 plan
          overlays the week (they have none of their own). Field reps see their own. */}
      {isCompanyView && reps.length > 0 && (
        <div className="flex items-center gap-2 mb-2 text-xs">
          <span className="text-slate-500 font-semibold whitespace-nowrap">1-on-1 plan:</span>
          <select value={commitUserId ?? ""} onChange={(e) => setCommitUserId(e.target.value)}
            className="flex-1 min-w-0 rounded-lg border border-slate-300 px-2 py-1.5 text-xs font-semibold bg-white">
            {reps.map((r) => <option key={r.id} value={r.id}>{r.full_name}</option>)}
          </select>
        </div>
      )}

      <div className="grid grid-cols-7 gap-1.5 lg:gap-3 mb-2 lg:mb-4">
        {days.map((dt, i) => {
          const today = isCurrentWeek && isoDate(dt) === todayISO;
          const selected = i === selDay;
          const items = sched[i] ?? [];
          const meets = items.filter((x) => x.t !== "build").length;
          const builds = items.filter((x) => x.t === "build").length;
          return (
            <button key={i} onClick={() => setSelDay(i)}
              className={"rounded-xl px-1 pt-1.5 pb-2 lg:pt-3 lg:pb-3 text-center transition " + (today ? "bg-bardin-orange text-white shadow-sm" : "bg-white border hover:border-slate-300 " + (selected ? "border-bardin-ink border-2" : "border-slate-200"))}>
              <div className={"text-[10px] lg:text-xs font-bold uppercase " + (today ? "text-white/90" : "text-slate-400")}>{DOW[i]}</div>
              <div className={"text-base lg:text-2xl font-bold " + (today ? "text-white" : "text-bardin-ink")}>{dt.getDate()}</div>
              <div className="flex flex-wrap justify-center gap-0.5 lg:gap-1 mt-1 lg:mt-2 min-h-[6px]">
                {Array.from({ length: Math.min(meets, 3) }).map((_, k) => <span key={"m" + k} className={"w-1.5 h-1.5 rounded-full " + (today ? "bg-white" : "bg-bardin-teal")} />)}
                {Array.from({ length: Math.min(builds, 3) }).map((_, k) => <span key={"b" + k} className={"w-1.5 h-1.5 rounded-full " + (today ? "bg-orange-200" : "bg-bardin-orange")} />)}
              </div>
              {/* desktop mini-agenda — preview the day's first appointments */}
              <div className="hidden lg:block text-left mt-2 min-h-[30px]">
                {items.length === 0 ? (
                  <div className={"text-[10px] " + (today ? "text-white/60" : "text-slate-300")}>—</div>
                ) : (
                  <>
                    {items.slice(0, 2).map((it, k) => (
                      <div key={k} className={"text-[10px] leading-tight truncate " + (today ? "text-white/90" : "text-slate-500")}>
                        <span className="font-bold">{it.t === "build" ? "🔨" : it.time || "·"}</span> {it.ho.split(" ")[0]}
                      </div>
                    ))}
                    {items.length > 2 && <div className={"text-[9px] font-semibold " + (today ? "text-white/70" : "text-slate-400")}>+{items.length - 2} more</div>}
                  </>
                )}
              </div>
              {!!commitUserId && (
                <div className="flex flex-col gap-0.5 mt-1.5">
                  {BLOCK_LETTER.map(([blk, ltr]) => (
                    <span key={blk} className={"text-[8px] font-extrabold rounded-sm leading-none py-0.5 " + cellClass(commitState[i]?.[blk], today)}>{ltr}</span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
      </>
      )}

      {isMonth && (
      <>
      {/* month nav */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setMonthOffset((o) => Math.max(o - 1, -6))} disabled={monthOffset <= -6}
          className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-lg disabled:opacity-30">‹</button>
        <div className="text-sm font-extrabold text-bardin-ink">{MONTHS[monthBase.getMonth()]} {monthBase.getFullYear()}</div>
        <button onClick={() => setMonthOffset((o) => Math.min(o + 1, 6))} disabled={monthOffset >= 6}
          className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-lg disabled:opacity-30">›</button>
      </div>
      {/* month grid */}
      <div className="grid grid-cols-7 gap-1 lg:gap-1.5 mb-2 lg:mb-4">
        {DOW.map((d) => <div key={"h" + d} className="text-center text-[10px] font-bold uppercase text-slate-400 pb-1">{d}</div>)}
        {monthCells.map((dt, i) => {
          const iso = isoDate(dt);
          const inMonth = dt.getMonth() === monthBase.getMonth();
          const today = iso === todayISO;
          const selected = iso === selMonthISO;
          const items = monthSched[iso] ?? [];
          const meets = items.filter((x) => x.t !== "build").length;
          const builds = items.filter((x) => x.t === "build").length;
          return (
            <button key={i} onClick={() => setSelMonthISO(iso)}
              className={"rounded-lg min-h-[48px] lg:min-h-[80px] p-1 lg:p-1.5 text-left align-top transition border " + (today ? "bg-bardin-orange text-white border-bardin-orange shadow-sm" : (selected ? "border-bardin-ink border-2 " : "border-slate-200 ") + (inMonth ? "bg-white hover:border-slate-300" : "bg-slate-50 opacity-60"))}>
              <div className={"text-[11px] lg:text-sm font-bold " + (today ? "text-white" : inMonth ? "text-bardin-ink" : "text-slate-400")}>{dt.getDate()}</div>
              <div className="flex flex-wrap gap-0.5 mt-0.5">
                {Array.from({ length: Math.min(meets, 4) }).map((_, k) => <span key={"m" + k} className={"w-1.5 h-1.5 rounded-full " + (today ? "bg-white" : "bg-bardin-teal")} />)}
                {Array.from({ length: Math.min(builds, 4) }).map((_, k) => <span key={"b" + k} className={"w-1.5 h-1.5 rounded-full " + (today ? "bg-orange-200" : "bg-bardin-orange")} />)}
              </div>
              {(meets + builds) > 0 && (
                <div className={"hidden lg:block text-[9px] font-semibold mt-1 leading-tight " + (today ? "text-white/80" : "text-slate-400")}>
                  {meets ? `${meets} mtg` : ""}{meets && builds ? " · " : ""}{builds ? `${builds} bld` : ""}
                </div>
              )}
            </button>
          );
        })}
      </div>
      </>
      )}

      {/* desktop dashboard split: schedule (left) + attention (right).
          The My Business band moved BELOW the widget board (Ship 1) — Greg:
          "set up to get to activity, to work, and then they can review." */}
      <div className="lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
      <div className="lg:col-span-8">

      {/* day detail */}
      <div className="text-xs font-bold text-slate-600 mb-1.5">
        {selIsToday ? "Today · " : ""}{DOW[selDow]} {MONTHS[selDate.getMonth()]} {selDate.getDate()} ·{" "}
        {selMeet || selBuild ? [selMeet ? `${selMeet} adjuster meeting${selMeet > 1 ? "s" : ""}` : "", selBuild ? `${selBuild} build${selBuild > 1 ? "s" : ""}` : ""].filter(Boolean).join(" · ") : "nothing scheduled"}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-5">
        {loadingSel ? (
          <div className="px-4 py-4 text-sm text-slate-400 text-center">Loading…</div>
        ) : selItems.length === 0 ? (
          <div className="px-4 py-4 text-sm text-slate-400 text-center">No company schedule items this day</div>
        ) : selItems.map((it, k) => (
          <Link key={k} to={`/deal/${it.dealId}`} className="flex items-start gap-2.5 px-3 py-2.5 lg:px-4 lg:py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition">
            <span className={"w-6 h-6 lg:w-9 lg:h-9 rounded-md grid place-items-center text-sm lg:text-lg shrink-0 " + (it.t === "meet" ? "bg-cyan-100" : it.t === "reinsp" ? "bg-purple-100" : "bg-orange-100")}>{it.t === "build" ? "🔨" : "🏠"}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm lg:text-base font-semibold truncate">{it.ho}{it.time ? ` · ${it.time}` : ""}{it.t === "build" ? ` · Build${it.dayOf ? `, ${it.dayOf}` : ""}` : it.t === "reinsp" ? " · 2nd Adjuster" : ""}</div>
              {it.addr && <div className="text-[11px] lg:text-xs text-slate-500 truncate">📍 {it.addr}</div>}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                {(it.ownerId || it.splitId) && (
                  <span className="text-[11px] lg:text-xs text-slate-400 truncate">
                    {it.ownerId ? `RM ${(roster[it.ownerId] ?? "—").split(" ")[0]}` : ""}
                    {it.splitId ? ` · split ${(roster[it.splitId] ?? "—").split(" ")[0]}` : ""}
                  </span>
                )}
                {it.carrier && <span className="hidden lg:inline text-xs text-slate-500">🏷 {it.carrier}</span>}
                {it.stage && <span className="hidden lg:inline text-[11px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{prettyStage(it.stage)}</span>}
                {it.claimNo && <span className="hidden lg:inline text-[11px] text-slate-400">#{it.claimNo}</span>}
              </div>
            </div>
            <span className="hidden lg:block self-center text-slate-300 text-lg">›</span>
          </Link>
        ))}
      </div>

      </div>{/* end left column */}

      {/* attention — sticky sidebar on desktop */}
      <div className="lg:col-span-4 lg:sticky lg:top-24">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Needs your attention</span>
        {toAddress > 0 && <span className="text-[11px] font-extrabold rounded-full bg-bardin-orange text-white px-2 py-0.5">{toAddress} to address</span>}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden mb-6">
        {attn.length === 0 ? (
          <div className="px-4 py-5 text-center text-sm text-slate-400">✓ All clear — nothing waiting on you</div>
        ) : attn.map((a, i) => (
          <Link key={i} to={a.to} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
            <span className={"w-8 h-8 rounded-lg grid place-items-center text-base shrink-0 " + (a.tone === "rose" ? "bg-rose-100" : a.tone === "amber" ? "bg-amber-100" : "bg-cyan-100")}>{a.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{a.t1}</div>
              <div className="text-[11px] text-slate-500">{a.t2}</div>
            </div>
            <span className="text-lg font-extrabold text-bardin-ink">{a.count}</span>
            <span className="text-slate-300 text-lg">›</span>
          </Link>
        ))}
      </div>
      </div>{/* end attention column */}

      </div>{/* end desktop dashboard split */}

      {/* Widget board — the action layer (Ship 1). Role-preloaded, no
          customize UI; registry lives in ./widgets/registry.ts. */}
      <WidgetBoard
        memberId={member.id}
        role={role}
        email={member.email}
        scopeIds={pipelineScopeIds}
        scopeNote={pipelineScopeNote}
        customKeys={customKeys}
      />

      {/* 💼 My Business — the review layer, below the board. Absorbs the old
          "This week" glance (meetings/builds/commitment/streak) plus the
          self-scoped money + funnel + crew snapshot. */}
      <div className="mt-6">
        <MyBusinessSummary
          member={member}
          role={role}
          core={snapCore}
          snapshotError={snapError}
          periodLabel={periodLabel}
          periodMeet={periodMeet}
          periodBuild={periodBuild}
          commitStats={!isMonth && commitUserId ? commitStats : null}
          commitLabel={commitRepName ? `${commitRepName}'s 1-on-1` : "Your 1-on-1"}
          streak={streak}
          showCommit={!isMonth && !!commitUserId}
          weekFinal={weekOffset < 0}
        />
      </div>

      {/* 🎯 Commission goal — the year on a line (Sep 28, 2026). Same snapshot
          as the band above; the goal itself is picked at the Monday 1-on-1. */}
      {role && FIELD_ROLES.has(role) && (
        <div className="mt-4">
          <CommissionGoalChart memberId={member.id} member={member} core={snapCore} coreError={snapError} />
        </div>
      )}

    </div>
  );
}
